import { useState, useCallback } from "react";
import { motion, AnimatePresence, type PanInfo } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Props = {
  images: { src: string; alt: string; caption?: string }[];
};

export default function ImageSlideshow({ images }: Props) {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);

  const go = useCallback(
    (delta: number) => {
      setDirection(delta);
      setCurrent((prev) => (prev + delta + images.length) % images.length);
    },
    [images.length]
  );

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const swipeThreshold = 45;

    if (info.offset.x <= -swipeThreshold) {
      go(1);
    } else if (info.offset.x >= swipeThreshold) {
      go(-1);
    }
  };

  if (!images.length) return null;

  const prev = (current - 1 + images.length) % images.length;
  const next = (current + 1) % images.length;

  return (
    <div className="w-full select-none">
      {/* Mobile: one calm, full-width swipeable image */}
      <div className="relative overflow-hidden rounded-xl bg-zinc-100 shadow-lg md:hidden">
        <AnimatePresence custom={direction} initial={false} mode="popLayout">
          <motion.img
            key={current}
            src={images[current].src}
            alt={images[current].alt}
            loading="lazy"
            decoding="async"
            custom={direction}
            variants={{
              enter: (d: number) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0.75 }),
              center: { x: 0, opacity: 1 },
              exit: (d: number) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0.75 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeInOut" }}
            drag={images.length > 1 ? "x" : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.18}
            onDragEnd={handleDragEnd}
            className="block h-[280px] w-full object-contain sm:h-[340px]"
            draggable={false}
          />
        </AnimatePresence>
      </div>

      {/* Desktop: carousel track with side previews */}
      <div className="relative hidden items-center justify-center gap-3 overflow-hidden px-2 md:flex">

        {/* Previous image (peek left) */}
        {images.length > 1 && (
          <button
            onClick={() => go(-1)}
            aria-label="Vorheriges Bild"
            className="relative shrink-0 w-[28%] cursor-pointer group focus:outline-none"
            style={{ minWidth: "120px" }}
          >
            <div className="rounded-lg overflow-hidden shadow-sm" style={{ maxHeight: "340px" }}>
              <img
                src={images[prev].src}
                alt={images[prev].alt}
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
                style={{ maxHeight: "340px", objectFit: "contain" }}
                draggable={false}
              />
            </div>
            <div className="absolute inset-0 bg-white/40 group-hover:bg-white/20 transition-colors rounded-lg" />
          </button>
        )}

        {/* Center (active) image */}
        <div className="relative shrink-0 flex-1 rounded-xl overflow-hidden shadow-lg bg-zinc-100" style={{ maxWidth: "560px" }}>
          <AnimatePresence custom={direction} initial={false} mode="popLayout">
            <motion.img
              key={current}
              src={images[current].src}
              alt={images[current].alt}
              loading="lazy"
              decoding="async"
              custom={direction}
              variants={{
                enter: (d: number) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0.5 }),
                center: { x: 0, opacity: 1 },
                exit: (d: number) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0.5 }),
              }}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="w-full h-auto block"
              style={{ maxHeight: "400px", objectFit: "contain" }}
              draggable={false}
            />
          </AnimatePresence>
        </div>

        {/* Next image (peek right) */}
        {images.length > 1 && (
          <button
            onClick={() => go(1)}
            aria-label="Nächstes Bild"
            className="relative shrink-0 w-[28%] cursor-pointer group focus:outline-none"
            style={{ minWidth: "120px" }}
          >
            <div className="rounded-lg overflow-hidden shadow-sm" style={{ maxHeight: "340px" }}>
              <img
                src={images[next].src}
                alt={images[next].alt}
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
                style={{ maxHeight: "340px", objectFit: "contain" }}
                draggable={false}
              />
            </div>
            <div className="absolute inset-0 bg-white/40 group-hover:bg-white/20 transition-colors rounded-lg" />
          </button>
        )}
      </div>

      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-4">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); }}
              aria-label={`Bild ${i + 1}`}
              className={`rounded-full transition-all duration-200 ${
                i === current
                  ? "w-5 h-2 bg-primary"
                  : "w-2 h-2 bg-zinc-300 hover:bg-zinc-400"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
